import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CalendarDaysIcon,
    MapPinIcon,
    ArrowsRightLeftIcon,
    UserGroupIcon,
    GlobeAltIcon,
    CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getGeographicAnalytics, getRegionalAnalytics } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtNumber = (n: number | null | undefined) => {
    if (n == null) return "—";
    return new Intl.NumberFormat("en-NG").format(n);
};

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const GeographicAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const {
        data: geoData,
        isLoading: geoLoading,
        error: geoError,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ["analytics-geographic", dateRange],
        queryFn: () => getGeographicAnalytics(dateRange),
    });

    const {
        data: regionData,
        isLoading: regionLoading,
        error: regionError,
    } = useQuery({
        queryKey: ["analytics-regions", dateRange],
        queryFn: () => getRegionalAnalytics(dateRange),
    });

    const geo = geoData?.data;
    const regions = regionData?.data;
    const pickupAreas = geo?.pickupHotspots ?? geo?.popularPickupAreas ?? [];
    const dropoffAreas = geo?.dropoffHotspots ?? geo?.popularDropoffAreas ?? [];
    const topRoutes = geo?.topRoutes ?? geo?.popularRoutes ?? [];
    const rideDensityAreas = geo?.rideDensityAreas ?? [];

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (geoLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (geoError) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load geographic analytics
                </h3>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date Range */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Popular Pickup Areas */}
            {pickupAreas.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <MapPinIcon className="h-4 w-4 text-emerald-500" /> Popular Pickup Areas
                    </h3>
                    <div className="space-y-2">
                        {pickupAreas.map((area) => {
                            const max = Math.max(...pickupAreas.map((a) => a.tripCount), 1);
                            const pct = (area.tripCount / max) * 100;
                            return (
                                <div key={area.name} className="flex items-center gap-3">
                                    <span className="w-36 truncate text-sm font-medium text-slate-900 dark:text-white">
                                        {area.name}
                                    </span>
                                    <div className="flex-1">
                                        <div className="h-4 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                            <div
                                                className="h-full rounded-full bg-emerald-500"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="w-28 text-right text-sm text-slate-500 dark:text-slate-400">
                                        {fmtNumber(area.tripCount)} ({area.percentage?.toFixed(1)}%)
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Popular Dropoff Areas */}
            {dropoffAreas.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <MapPinIcon className="h-4 w-4 text-rose-500" /> Popular Dropoff Areas
                    </h3>
                    <div className="space-y-2">
                        {dropoffAreas.map((area) => {
                            const max = Math.max(...dropoffAreas.map((a) => a.tripCount), 1);
                            const pct = (area.tripCount / max) * 100;
                            return (
                                <div key={area.name} className="flex items-center gap-3">
                                    <span className="w-36 truncate text-sm font-medium text-slate-900 dark:text-white">
                                        {area.name}
                                    </span>
                                    <div className="flex-1">
                                        <div className="h-4 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                            <div
                                                className="h-full rounded-full bg-rose-400"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="w-28 text-right text-sm text-slate-500 dark:text-slate-400">
                                        {fmtNumber(area.tripCount)} ({area.percentage?.toFixed(1)}%)
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Popular Routes */}
            {topRoutes.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ArrowsRightLeftIcon className="h-4 w-4 text-blue-500" /> Popular Routes
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Origin</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Destination</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Trips</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Avg Fare</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Avg Duration</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {topRoutes.map((route, i) => (
                                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{route.origin}</td>
                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{route.destination}</td>
                                        <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{fmtNumber(route.tripCount)}</td>
                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{fmtCurrency(route.averageFare)}</td>
                                        <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{route.averageDuration?.toFixed(0)} min</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Ride Density Areas */}
            {rideDensityAreas.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <GlobeAltIcon className="h-4 w-4 text-cyan-500" /> Ride Density Areas
                    </h3>
                    <div className="space-y-2">
                        {rideDensityAreas.map((area) => {
                            const max = Math.max(...rideDensityAreas.map((item) => item.tripCount), 1);
                            const pct = (area.tripCount / max) * 100;
                            return (
                                <div key={`${area.name}-${area.latitude}-${area.longitude}`} className="flex items-center gap-3">
                                    <span className="w-36 truncate text-sm font-medium text-slate-900 dark:text-white">
                                        {area.name}
                                    </span>
                                    <div className="flex-1">
                                        <div className="h-4 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                            <div
                                                className="h-full rounded-full bg-cyan-500"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="w-28 text-right text-sm text-slate-500 dark:text-slate-400">
                                        {fmtNumber(area.tripCount)} ({area.percentage?.toFixed(1)}%)
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Driver Density Hotspots */}
            {geo?.driverDensityHotspots && geo.driverDensityHotspots.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <UserGroupIcon className="h-4 w-4 text-amber-500" /> Driver Density Hotspots
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Area</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Drivers</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Demand Ratio</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Coordinates</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {geo.driverDensityHotspots.map((spot) => (
                                    <tr key={spot.name} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{spot.name}</td>
                                        <td className="px-3 py-2 text-amber-600 dark:text-amber-400">{fmtNumber(spot.driverCount)}</td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${spot.demandRatio > 1
                                                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                                        : spot.demandRatio > 0.7
                                                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                    }`}
                                            >
                                                {spot.demandRatio?.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-400">
                                            {spot.latitude?.toFixed(4)}, {spot.longitude?.toFixed(4)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ================================================================
                Regional Analytics
               ================================================================ */}
            <div className="border-t border-slate-200 pt-6 dark:border-slate-700">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                    <GlobeAltIcon className="h-5 w-5 text-primary-500" /> Regional Analytics
                </h2>

                {regionLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : regionError ? (
                    <p className="text-sm text-rose-500">Failed to load regional data</p>
                ) : regions ? (
                    <div className="space-y-5">
                        {/* Cross-region stats */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <p className="text-sm text-slate-500 dark:text-slate-400">Cross-Region Trips</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">
                                    {fmtNumber(regions.crossRegionTrips)}
                                </p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <p className="text-sm text-slate-500 dark:text-slate-400">Avg Cross-Region Fare</p>
                                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                                    {fmtCurrency(regions.averageCrossRegionFare)}
                                </p>
                            </div>
                        </div>

                        {/* Regions table */}
                        {regions.regions && regions.regions.length > 0 && (
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Region</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Trips</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Revenue</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Drivers</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Avg Fare</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Completion</th>
                                                <th className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Growth</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {regions.regions.map((r) => (
                                                <tr key={r.name} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{r.name}</td>
                                                    <td className="px-4 py-3 text-blue-600 dark:text-blue-400">{fmtNumber(r.tripCount)}</td>
                                                    <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400">{fmtCurrency(r.revenue)}</td>
                                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{fmtNumber(r.activeDrivers)}</td>
                                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{fmtCurrency(r.averageFare)}</td>
                                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{r.completionRate?.toFixed(1)}%</td>
                                                    <td className="px-4 py-3">
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${r.growthRate >= 0
                                                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                                    : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                                                }`}
                                                        >
                                                            {r.growthRate >= 0 ? "+" : ""}
                                                            {r.growthRate?.toFixed(1)}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
